import React, { useState } from 'react';
import { toast } from 'sonner';
import { Loader2, X, Bookmark, BookmarkCheck, Trash2, Facebook, Twitter, Linkedin, Instagram, Youtube, Globe } from 'lucide-react';
import { opportunitiesAPI } from '../lib/api';
import { BACKEND_URL as API } from '../lib/config';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';

// Legacy parity for the opportunity detail (s158) — port of the blocks of
// my-account2/current_projects_description.php that the Moderno detail lacked:
// key figures, Fund it / Pass / Follow, tier cards with «Select contribution»,
// Investors / Backers lists, the Author block and Ask a Question.
// Data comes from routes/opportunity_community.py::public_extras.

const HEAD = 'var(--op-head)';
const INK = 'var(--color-heading, #1a2332)';
const line = 'rgba(20,26,58,.1)';
const faint = 'rgba(20,26,58,.55)';
const img = (s) => (s && s.startsWith('/api') ? `${API}${s}` : s);
const money = (v) => Number(v || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const day = (iso) => {
  if (!iso) return '—';
  const d = new Date(String(iso).length === 10 ? `${iso}T00:00:00Z` : iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
};
const fullName = (p) => [p?.first_name, p?.last_name].filter(Boolean).join(' ') || 'Member';
const initials = (p) => `${(p?.first_name || '')[0] || ''}${(p?.last_name || '')[0] || ''}`.toUpperCase() || '?';
const SOCIAL = { facebook: Facebook, twitter: Twitter, x: Twitter, linkedin: Linkedin, instagram: Instagram, youtube: Youtube };
const btn = 'h-11 px-4 rounded-[var(--op-radius)] border text-sm font-semibold inline-flex items-center justify-center gap-2 transition-transform active:scale-[.97]';

export function Avatar({ p, size = 44 }) {
  return p?.avatar
    ? <img src={img(p.avatar)} alt="" className="rounded-full object-cover flex-none" style={{ width: size, height: size }} />
    : <span className="rounded-full grid place-items-center font-bold text-white flex-none" style={{ width: size, height: size, background: INK, fontSize: size / 3.2 }}>{initials(p)}</span>;
}

/* Key figures — the legacy right column (type, dates, investors, backers, funded, days to go). */
export function KeyFigures({ o }) {
  const ends = o.funding_ends_at ? new Date(o.funding_ends_at) : null;
  const daysLeft = ends ? Math.max(0, Math.ceil((ends - Date.now()) / 86400000)) : null;
  const pct = o.total_amount ? Math.min(100, Math.round(((o.committed_total || 0) / o.total_amount) * 100)) : 0;
  const cells = [
    ['Opportunity type', o.type_name || '—'],
    ['Funding ends', day(o.funding_ends_at || o.dates?.funding_end)],
    ['Launched', day(o.dates?.launch || o.published_at)],
    ['Project date', day(o.dates?.project)],
    ['Investors', String(o.investors_count || 0)],
    ['Backers', String(o.backers_people_count || 0)],
    ['Funded', `${money(o.committed_total)} of ${money(o.total_amount)}`],
    ['Days to go', daysLeft == null ? '—' : String(daysLeft)],
  ];
  return (
    <div className="mt-6" data-testid="opportunity-key-figures">
      <dl className="grid grid-cols-2 md:grid-cols-4 border-t border-l" style={{ borderColor: line }}>
        {cells.map(([k, v]) => (
          <div key={k} className="px-4 py-3.5 border-r border-b min-w-0" style={{ borderColor: line }}>
            <dt className="text-[11px] font-semibold uppercase" style={{ letterSpacing: '.08em', color: faint }}>{k}</dt>
            <dd className="mt-1 font-bold tabular-nums truncate" style={{ color: INK, fontFamily: HEAD, fontSize: '1.05rem' }}>{v}</dd>
          </div>
        ))}
      </dl>
      <div className="h-1.5 rounded-full mt-3" style={{ background: 'rgba(20,26,58,.08)' }} aria-label={`${pct}% funded`}>
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: 'var(--color-accent, #ff9c1b)' }} />
      </div>
    </div>
  );
}

/* Fund it / Pass / Follow + «Back this project» (scrolls to the tier cards). */
export function ActionRow({ o, onChange }) {
  const [busy, setBusy] = useState('');
  if (o.viewer_is_owner) return null;
  const toggle = async (key) => {
    setBusy(key);
    try {
      const next = !o[`viewer_${key}`];
      await opportunitiesAPI.mark(o.slug, { [key]: next });
      onChange({ [`viewer_${key}`]: next });
      toast.success(key === 'follow' ? (next ? 'Added to your watchlist' : 'Removed from your watchlist')
        : (next ? 'Hidden from your opportunities list' : 'Back in your opportunities list'));
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not update'); }
    finally { setBusy(''); }
  };
  const fund = () => window.dispatchEvent(new CustomEvent('fundit:open', { detail: {} }));
  const back = () => document.getElementById('offerings')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  return (
    <div className="mt-4 grid gap-2" data-testid="opportunity-actions">
      <div className="grid grid-cols-3 gap-2">
        <button type="button" className={btn} style={{ background: INK, color: '#fff', borderColor: INK }} onClick={fund} disabled={!o.fund_it || !o.funding_open}>Fund it</button>
        <button type="button" className={btn} style={{ borderColor: line, color: INK, background: o.viewer_pass ? 'rgba(20,26,58,.06)' : '#fff' }} onClick={() => toggle('pass')} aria-pressed={o.viewer_pass}>
          {busy === 'pass' ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}{o.viewer_pass ? 'Passed' : 'Pass'}
        </button>
        <button type="button" className={btn} style={{ borderColor: line, color: INK, background: o.viewer_follow ? 'rgba(20,26,58,.06)' : '#fff' }} onClick={() => toggle('follow')} aria-pressed={o.viewer_follow}>
          {busy === 'follow' ? <Loader2 className="w-4 h-4 animate-spin" /> : o.viewer_follow ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}{o.viewer_follow ? 'Following' : 'Follow'}
        </button>
      </div>
      {(o.backers || []).length + (o.benefits || []).length > 0 && (
        <button type="button" className={btn} style={{ borderColor: line, color: INK }} onClick={back}>Back this project</button>
      )}
    </div>
  );
}

/* One tier card: hover shows «Select contribution»; click opens the amount + Continue. */
function TierCard({ r, count, unit, canFund }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(String(r.price || ''));
  const left = r.stock != null ? Math.max(0, Number(r.stock) - count) : null;
  const soldOut = left === 0;
  const go = (e) => {
    e.preventDefault();
    window.dispatchEvent(new CustomEvent('fundit:open', { detail: { pledge_id: r.id, amount: Number(amount) || r.price } }));
  };
  const head = (
    <>
      <span className="block font-semibold" style={{ fontFamily: HEAD, fontSize: '1.15rem', color: INK }}>{r.title}</span>
      {r.price ? <span className="block text-sm font-semibold mt-0.5" style={{ color: 'var(--op-strong)' }}>Contribute {money(r.price)} or more</span> : null}
      {r.description && <div className="mod-prose !text-[14.5px] !leading-relaxed mt-2" dangerouslySetInnerHTML={{ __html: r.description }} />}
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-[13px] font-semibold">
        {r.stock != null && <span style={{ color: soldOut ? '#b42318' : '#b54708' }}>{soldOut ? 'Sold out' : `Limited stock: ${left} of ${Number(r.stock).toLocaleString('en-US')} left`}</span>}
        <span style={{ color: faint }}>{count} {unit}{count === 1 ? '' : 's'}</span>
      </div>
    </>
  );
  if (!canFund || soldOut) {
    return <div className="rounded-[var(--op-radius)] border p-5" style={{ borderColor: line }}>{head}</div>;
  }
  return (
    <div className="op-tier rounded-[var(--op-radius)] border overflow-hidden" style={{ borderColor: open ? INK : line }}>
      <button type="button" className="relative block w-full text-left p-5" onClick={() => setOpen(v => !v)} aria-expanded={open}>
        {head}
        {!open && <span className="op-tier-hover absolute inset-0 grid place-items-center text-white font-semibold text-lg" style={{ background: 'color-mix(in srgb, var(--op-dark, #141416) 88%, transparent)' }}>Select contribution</span>}
      </button>
      {open && (
        <form onSubmit={go} className="border-t p-5 grid gap-3" style={{ borderColor: line }}>
          <label className="grid gap-1.5 text-[11px] font-bold uppercase" style={{ letterSpacing: '.08em', color: INK }}>Amount of contribution
            <span className="flex items-center gap-2 border-b pb-1" style={{ borderColor: line }}>
              <span className="text-lg font-bold" style={{ color: INK }}>$</span>
              <input type="number" min={r.price || 1} step="any" required value={amount} onChange={e => setAmount(e.target.value)}
                className="w-full h-10 text-base font-normal tabular-nums outline-none bg-transparent" style={{ color: INK }} />
            </span>
          </label>
          <button type="submit" className={btn} style={{ background: INK, color: '#fff', borderColor: INK }}>Continue</button>
        </form>
      )}
    </div>
  );
}

const TIER_CSS = `
.op-tier-hover{opacity:0;transition:opacity 180ms cubic-bezier(.23,1,.32,1)}
@media (hover:hover) and (pointer:fine){.op-tier button:hover .op-tier-hover{opacity:1}}
.op-tier button:focus-visible .op-tier-hover{opacity:1}
@media (prefers-reduced-motion:reduce){.op-tier-hover{transition:none}}
`;

/* Offerings with the legacy tabs: Investment benefits · Backers pledge · Services. */
export function TierTabs({ o }) {
  const groups = [
    ['benefits', 'Investment benefits', 'investor'],
    ['backers', 'Backers pledge', 'backer'],
    ['services', 'Services', 'client'],
  ].filter(([k]) => (o[k] || []).length);
  const [tab, setTab] = useState(groups[0]?.[0]);
  if (!groups.length) return null;
  const current = groups.find(g => g[0] === tab) || groups[0];
  const canFund = !!o.fund_it && !!o.funding_open && !o.viewer_is_owner;
  return (
    <div>
      <style>{TIER_CSS}</style>
      <div role="tablist" className="inline-flex border rounded-[var(--op-radius)] overflow-hidden mb-4" style={{ borderColor: line }}>
        {groups.map(([k, label]) => (
          <button key={k} type="button" role="tab" aria-selected={k === current[0]} onClick={() => setTab(k)}
            className="h-10 px-4 text-sm font-semibold" style={k === current[0] ? { background: INK, color: '#fff' } : { color: faint }}>{label}</button>
        ))}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {o[current[0]].map(r => <TierCard key={r.id} r={r} count={(o.tier_counts || {})[r.id] || 0} unit={current[2]} canFund={canFund} />)}
      </div>
    </div>
  );
}

/* Investors / Backers — people only, never amounts. */
export function PeopleList({ people, empty }) {
  if (!people.length) return <p className="text-sm" style={{ color: faint }}>{empty}</p>;
  return (
    <ul className="grid gap-3 sm:grid-cols-2">
      {people.map(p => (
        <li key={p.member_id} className="flex items-center gap-3">
          <Avatar p={p} />
          <div className="min-w-0">
            <div className="font-semibold truncate" style={{ color: INK }}>{fullName(p)}</div>
            <div className="text-[13px]" style={{ color: faint }}>{[p.membership_id, p.since && `since ${day(p.since)}`].filter(Boolean).join(' · ')}</div>
          </div>
        </li>
      ))}
    </ul>
  );
}

function Socials({ list }) {
  if (!list?.length) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {list.map(s => {
        const Icon = SOCIAL[String(s.platform || '').toLowerCase()] || Globe;
        return (
          <a key={s.platform + s.url} href={s.url} target="_blank" rel="noopener noreferrer" aria-label={s.platform}
            className="w-9 h-9 grid place-items-center rounded-full border" style={{ borderColor: line, color: INK }}>
            <Icon className="w-4 h-4" />
          </a>
        );
      })}
    </div>
  );
}

/* Author: photo, name, projects, summary + Contact me / Profile / Full bio / Our team / FAQ. */
export function AuthorBlock({ o, hasTeam, hasFaq }) {
  const a = o.author_profile;
  const [dlg, setDlg] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  if (!a) return null;
  const jump = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await opportunitiesAPI.contactAuthor(o.slug, msg); toast.success('Message sent'); setMsg(''); setDlg(''); }
    catch (ex) { toast.error(ex.response?.data?.detail || 'Could not send the message'); }
    finally { setBusy(false); }
  };
  const place = [a.city, a.country].filter(Boolean).join(', ');
  const buttons = [
    !o.viewer_is_owner && ['Contact me', () => setDlg('contact')],
    ['Profile', () => setDlg('profile')],
    a.biography && ['Full bio', () => setDlg('bio')],
    hasTeam && ['Our team', () => jump('team')],
    hasFaq && ['FAQ', () => jump('faq')],
  ].filter(Boolean);
  return (
    <div className="grid gap-6 sm:grid-cols-[160px_minmax(0,1fr)] items-start">
      <div className="flex flex-col items-center gap-3">
        <Avatar p={a} size={150} />
        <Socials list={a.socials} />
      </div>
      <div className="min-w-0">
        <h3 className="font-semibold" style={{ fontFamily: HEAD, fontSize: '1.6rem', color: INK }}>{fullName(a)}</h3>
        <p className="text-sm font-semibold mt-0.5" style={{ color: faint }}>{a.projects} project{a.projects === 1 ? '' : 's'}{place ? ` · ${place}` : ''}</p>
        {(a.summary || a.biography) && <div className="mod-prose !text-[15px] !leading-relaxed mt-3" dangerouslySetInnerHTML={{ __html: a.summary || a.biography }} />}
        <div className="flex flex-wrap gap-2 mt-4">
          {buttons.map(([label, fn]) => <button key={label} type="button" className={btn} style={{ borderColor: line, color: INK }} onClick={fn}>{label}</button>)}
        </div>
      </div>

      <Dialog open={!!dlg} onOpenChange={(v) => !v && setDlg('')}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{dlg === 'contact' ? `Contact ${fullName(a)}` : dlg === 'bio' ? `Biography: ${fullName(a)}` : fullName(a)}</DialogTitle>
            <DialogDescription>{dlg === 'contact' ? 'Your message goes to the author by email with your contact details.' : (a.membership_id || '')}</DialogDescription>
          </DialogHeader>
          {dlg === 'contact' && (
            <form onSubmit={send} className="grid gap-3">
              <textarea required rows={5} maxLength={4000} value={msg} onChange={e => setMsg(e.target.value)} className="px-3 py-2 border rounded-md" placeholder="Write your message" />
              <button type="submit" disabled={busy} className={btn} style={{ background: INK, color: '#fff', borderColor: INK }}>{busy && <Loader2 className="w-4 h-4 animate-spin" />}Send message</button>
            </form>
          )}
          {dlg === 'profile' && (
            <div className="grid gap-4">
              <div className="flex items-center gap-4"><Avatar p={a} size={64} />
                <dl className="text-sm grid gap-1">
                  {place && <div><dt className="inline font-semibold">Location: </dt><dd className="inline">{place}</dd></div>}
                  <div><dt className="inline font-semibold">Member since: </dt><dd className="inline">{day(a.member_since)}</dd></div>
                  <div><dt className="inline font-semibold">Published projects: </dt><dd className="inline">{a.projects}</dd></div>
                </dl>
              </div>
              {a.summary && <div className="mod-prose !text-[15px]" dangerouslySetInnerHTML={{ __html: a.summary }} />}
              <Socials list={a.socials} />
            </div>
          )}
          {dlg === 'bio' && <div className="mod-prose !text-[15px] max-h-[60vh] overflow-y-auto" dangerouslySetInnerHTML={{ __html: a.biography }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* Ask a Question: public thread under the opportunity; the author is emailed. */
export function Questions({ o, onChange }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const list = o.questions || [];
  const ask = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await opportunitiesAPI.ask(o.slug, text);
      const r = await opportunitiesAPI.detail(o.slug);
      onChange({ questions: r.data.questions });
      setText('');
      toast.success('Question posted');
    } catch (ex) { toast.error(ex.response?.data?.detail || 'Could not post the question'); }
    finally { setBusy(false); }
  };
  const remove = async (q) => {
    try { await opportunitiesAPI.deleteQuestion(q.id); onChange({ questions: list.filter(x => x.id !== q.id) }); }
    catch (ex) { toast.error(ex.response?.data?.detail || 'Could not delete'); }
  };
  return (
    <div className="grid gap-5">
      {list.length === 0 && <p className="text-sm" style={{ color: faint }}>No questions yet. Be the first to ask.</p>}
      {list.map(q => (
        <div key={q.id} className="flex gap-3">
          <Avatar p={q.author} size={40} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-sm">
              <b style={{ color: INK }}>{fullName(q.author)}</b><span style={{ color: faint }}>{day(q.created_at)}</span>
              {q.can_delete && <button type="button" onClick={() => remove(q)} aria-label="Delete question" className="ml-auto p-1" style={{ color: faint }}><Trash2 className="w-4 h-4" /></button>}
            </div>
            <p className="text-[15px] leading-relaxed mt-1 whitespace-pre-line" style={{ color: 'var(--color-body-text, #475569)' }}>{q.comment}</p>
          </div>
        </div>
      ))}
      {!o.viewer_is_owner && (
        <form onSubmit={ask} className="grid gap-2">
          <textarea required rows={3} maxLength={2000} value={text} onChange={e => setText(e.target.value)} className="px-3 py-2 border rounded-md" placeholder="Ask the author a question" />
          <button type="submit" disabled={busy} className={`${btn} justify-self-start`} style={{ background: INK, color: '#fff', borderColor: INK }}>{busy && <Loader2 className="w-4 h-4 animate-spin" />}Post question</button>
        </form>
      )}
    </div>
  );
}

